/**
 * ProShip Direct v2 — the numbers behind the performance comparison.
 *
 * ALL OF THIS IS ILLUSTRATIVE, and the reason is a real backend gap worth stating precisely: there
 * is no owner dimension on the shipment aggregations. Nothing records whether a given shipment went
 * out on the merchant's own contract or on ProShip's, so no query can produce this page today. A
 * real version needs allocation to stamp the pool (and ideally the reason) on every shipment, after
 * which every figure here becomes a group-by.
 *
 * Generated rather than hard-coded, for one reason: a date filter that does not change the numbers
 * is a lie, and hard-coded fixtures make it one. The generator is a deterministic PRNG seeded by
 * the window, so the same range always yields the same numbers — no jitter on re-render — while a
 * different range yields a coherently different set.
 *
 * WHAT MAKES A COMPARISON WORTH SHOWING, since this page could have thirty of them: it has to be a
 * cut where the answer plausibly flips. A merchant's own contract is rarely better or worse
 * outright — it is better in metros and worse in the north-east, better under 500g and worse above
 * two kilos, better on prepaid and worse on COD. Those are the cuts here. An aggregate that only
 * ever says "you are 1.8 points behind" tells them nothing they can act on.
 */

/** Date windows offered above the page. */
export const RANGE_PRESETS = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '180d', label: 'Last 6 months', days: 180 },
];

export const ZONES = ['Zone 1', 'Zone 2', 'Zone 3', 'Zone 4', 'Zone 5'];

export const WEIGHT_SLABS = ['Up to 500g', '500g – 1kg', '1 – 2kg', '2 – 5kg', 'Above 5kg'];

/** Deterministic PRNG — same seed, same numbers, so the page never jitters between renders. */
function seeded(seedText) {
  let h = 2166136261;
  for (let i = 0; i < String(seedText).length; i += 1) {
    h ^= String(seedText).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const round = (n, dp = 1) => Number(n.toFixed(dp));

/**
 * One period of Direct-vs-ProShip figures.
 *
 * `couriers` are the merchant's live accounts, so the per-courier cut names their real accounts
 * rather than invented ones. `withRateCard` decides whether cost comparison is available at all —
 * ProShip does not hold the merchant's rates, so an account without an uploaded card contributes
 * volume and service figures but no cost, and the page has to say so rather than quietly averaging
 * over a smaller base.
 */
export function buildPerformance({ days, couriers = [] }) {
  const rnd = seeded(`dp-${days}`);
  const pick = (min, max, dp = 1) => round(min + rnd() * (max - min), dp);

  const dailyOwn = 340 + Math.round(rnd() * 90);
  const dailyProship = 8100 + Math.round(rnd() * 1400);
  const ownShipments = dailyOwn * days;
  const proshipShipments = dailyProship * days;
  const total = ownShipments + proshipShipments;

  const ownFreight = pick(62, 70, 2);
  const proshipFreight = pick(78, 86, 2);
  const ownLanded = round(ownFreight + pick(20, 25, 2), 2);
  const proshipLanded = round(proshipFreight + pick(15, 19, 2), 2);

  const own = {
    shipments: ownShipments,
    delivery: pick(89.5, 92.5),
    firstAttempt: pick(81, 85),
    rto: pick(5.0, 6.4),
    freight: ownFreight,
    landed: ownLanded,
    tat: pick(2.6, 3.6, 1),
    eddMet: pick(86, 93),
    pickupSla: pick(93, 96.5),
    lost: pick(1.1, 2.4, 1),
  };

  const proship = {
    shipments: proshipShipments,
    delivery: pick(91.5, 93.5),
    firstAttempt: pick(83, 86),
    rto: pick(4.8, 5.8),
    freight: proshipFreight,
    landed: proshipLanded,
    tat: pick(3.0, 3.9, 1),
    eddMet: pick(88, 94),
    pickupSla: pick(91, 95),
    lost: pick(0.9, 1.9, 1),
  };

  /**
   * The full metric table.
   *
   * `better: 'low'` or `'high'` says which direction wins, so the gap column can colour itself
   * without the page hard-coding a list of "metrics where less is more".
   */
  const metrics = [
    { key: 'shipments', label: 'Shipments', own: own.shipments, proship: proship.shipments, fmt: 'int', better: null, group: 'Volume' },
    {
      key: 'share',
      label: 'Share of your volume',
      own: round((ownShipments / total) * 100),
      proship: round((proshipShipments / total) * 100),
      fmt: 'pct',
      better: null,
      group: 'Volume',
    },
    { key: 'delivery', label: 'Delivered', own: own.delivery, proship: proship.delivery, fmt: 'pct', better: 'high', group: 'Service' },
    { key: 'firstAttempt', label: 'Delivered first attempt', own: own.firstAttempt, proship: proship.firstAttempt, fmt: 'pct', better: 'high', group: 'Service' },
    { key: 'eddMet', label: 'Delivered by promised date', own: own.eddMet, proship: proship.eddMet, fmt: 'pct', better: 'high', group: 'Service' },
    { key: 'tat', label: 'Transit days (P2A)', own: own.tat, proship: proship.tat, fmt: 'num', better: 'low', group: 'Service' },
    { key: 'pickupSla', label: 'Pickup on time', own: own.pickupSla, proship: proship.pickupSla, fmt: 'pct', better: 'high', group: 'Service' },
    { key: 'rto', label: 'RTO', own: own.rto, proship: proship.rto, fmt: 'pct', better: 'low', group: 'Failure' },
    { key: 'lost', label: 'Lost or damaged per 10,000', own: own.lost, proship: proship.lost, fmt: 'num', better: 'low', group: 'Failure' },
    // No weight-dispute rows. A discrepancy raised against a Direct account is settled between the
    // merchant and their own carrier — ProShip is not party to it and never sees the figure, so a
    // comparison would have a real ProShip number against a blank, or worse, an invented one.
    { key: 'freight', label: 'Freight per shipment', own: own.freight, proship: proship.freight, fmt: 'inr', better: 'low', group: 'Cost' },
  ];

  /**
   * By zone. The cut that matters most: a merchant's own contract is usually competitive on the
   * metros it was negotiated for and weak on the long tail, and the aggregate hides both.
   */
  const zoneMix = [0.31, 0.24, 0.21, 0.17, 0.07];
  const zones = ZONES.map((zone, i) => {
    const zOwn = Math.round(ownShipments * zoneMix[i] * pick(0.85, 1.15, 3));
    const zProship = Math.round(proshipShipments * zoneMix[i] * pick(0.9, 1.1, 3));
    const penalty = i * pick(0.6, 1.3, 2); // the tail costs more and delivers worse, for both
    return {
      zone,
      ownShipments: zOwn,
      proshipShipments: zProship,
      ownDelivery: round(own.delivery + pick(1.5, 3) - penalty),
      proshipDelivery: round(proship.delivery + pick(1, 2.5) - penalty * 0.8),
      ownCost: round(ownLanded * (0.72 + i * 0.16), 2),
      proshipCost: round(proshipLanded * (0.76 + i * 0.15), 2),
      ownTat: round(own.tat * (0.6 + i * 0.24), 1),
      proshipTat: round(proship.tat * (0.62 + i * 0.22), 1),
    };
  });

  /** By payment mode. COD is where RTO lives, so an aggregate RTO number is close to meaningless. */
  const codShare = pick(0.52, 0.66, 3);
  const paymentModes = [
    {
      mode: 'COD',
      ownShipments: Math.round(ownShipments * codShare),
      proshipShipments: Math.round(proshipShipments * codShare),
      ownDelivery: round(own.delivery - pick(2, 3.5)),
      proshipDelivery: round(proship.delivery - pick(1.8, 3)),
      ownCost: round(ownLanded + pick(8, 14, 2), 2),
      proshipCost: round(proshipLanded + pick(9, 16, 2), 2),
    },
    {
      mode: 'Prepaid',
      ownShipments: Math.round(ownShipments * (1 - codShare)),
      proshipShipments: Math.round(proshipShipments * (1 - codShare)),
      ownDelivery: round(own.delivery + pick(2, 3.5)),
      proshipDelivery: round(proship.delivery + pick(1.8, 3)),
      ownCost: round(ownLanded - pick(3, 7, 2), 2),
      proshipCost: round(proshipLanded - pick(3, 7, 2), 2),
    },
  ];

  /**
   * By weight slab. Where a negotiated contract actually wins: the merchant's rate is usually cut
   * for the slab they ship most of, and uncompetitive either side of it.
   */
  const slabMix = [0.34, 0.27, 0.19, 0.13, 0.07];
  const weightSlabs = WEIGHT_SLABS.map((slab, i) => {
    const advantage = [1.32, 1.24, 1.06, 0.94, 0.86][i]; // own contract loses its edge as weight rises
    const proshipCost = round(proshipLanded * (0.7 + i * 0.42), 2);
    return {
      slab,
      ownShipments: Math.round(ownShipments * slabMix[i] * pick(0.9, 1.1, 3)),
      proshipShipments: Math.round(proshipShipments * slabMix[i] * pick(0.9, 1.1, 3)),
      ownCost: round((proshipCost / advantage) * pick(0.97, 1.03, 3), 2),
      proshipCost,
    };
  });

  /** Per Direct account, against the ProShip pool average. */
  const byCourier = couriers.map((c, i) => {
    const r = seeded(`${c.accountCode}-${days}`);
    const share = 0.5 / (i + 1);
    const shipments = Math.max(1, Math.round(ownShipments * share * (0.8 + r() * 0.4)));
    return {
      id: c.id,
      carrierName: c.carrierName,
      accountCode: c.accountCode,
      serviceType: c.serviceType,
      hasRateCard: c.config?.rateCard === 'OWN',
      shipments,
      delivery: round(own.delivery + (r() * 5 - 2.5)),
      firstAttempt: round(own.firstAttempt + (r() * 6 - 3)),
      rto: round(own.rto + (r() * 2 - 1)),
      inTransit: round(2.5 + r() * 4),
      tat: round(own.tat + (r() * 1.4 - 0.7), 1),
      cost: round(ownLanded + (r() * 18 - 9), 2),
      fallback: Math.round(r() * 140),
    };
  });

  /** Weekly trend, oldest first: is the gap opening or closing? */
  const buckets = Math.min(12, Math.max(4, Math.round(days / 7)));
  const trend = Array.from({ length: buckets }, (unused, i) => {
    const drift = i / Math.max(1, buckets - 1);
    return {
      label: `W${i + 1}`,
      directShare: round((ownShipments / total) * 100 * (0.7 + drift * 0.6)),
      ownCost: round(ownLanded * (1.06 - drift * 0.1), 2),
      proshipCost: round(proshipLanded * (1.02 - drift * 0.02), 2),
    };
  });

  /** Where the volume actually goes. Useful for spotting a state your contract does not serve. */
  const topStates = [
    'Maharashtra', 'Karnataka', 'Delhi', 'Tamil Nadu', 'Uttar Pradesh',
    'Gujarat', 'Telangana', 'West Bengal',
  ].map((state, i) => {
    const r = seeded(`${state}-${days}`);
    const weight = [0.19, 0.15, 0.13, 0.11, 0.1, 0.09, 0.07, 0.06][i];
    return {
      state,
      ownShipments: Math.round(ownShipments * weight * (0.8 + r() * 0.5)),
      proshipShipments: Math.round(proshipShipments * weight * (0.9 + r() * 0.2)),
      ownDelivery: round(own.delivery + (r() * 6 - 3)),
      proshipDelivery: round(proship.delivery + (r() * 4 - 2)),
    };
  });

  /** Orders that meant to ship Direct and fell back to ProShip, by carrier. */
  const fallback = couriers.map((c) => {
    const r = seeded(`fb-${c.accountCode}-${days}`);
    return { name: c.carrierName, code: c.accountCode, value: Math.round(r() * 160 * (days / 30)) };
  });

  /**
   * Money saved against what ProShip would have charged.
   *
   * Only computable for accounts with an uploaded rate card — ProShip does not hold the merchant's
   * rates. `coverage` is the share of Direct volume the figure actually covers, so the page can say
   * "based on 2 of your 3 accounts" instead of presenting a partial number as a total.
   */
  const withCard = byCourier.filter((c) => c.hasRateCard);
  const coveredShipments = withCard.reduce((sum, c) => sum + c.shipments, 0);
  const countedShipments = byCourier.reduce((sum, c) => sum + c.shipments, 0) || 1;

  const savings = {
    perShipment: round(proshipLanded - ownLanded, 2),
    total: Math.round((proshipLanded - ownLanded) * coveredShipments),
    coveredShipments,
    coverage: round((coveredShipments / countedShipments) * 100),
    accountsWithCard: withCard.length,
    accountsTotal: byCourier.length,
  };

  return {
    days,
    own,
    proship,
    total,
    metrics,
    zones,
    paymentModes,
    weightSlabs,
    byCourier,
    trend,
    topStates,
    fallback,
    savings,
  };
}

/** Formats a metric value for display, by its declared format. */
export function formatMetric(fmt, value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (fmt === 'inr') return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  if (fmt === 'pct') return `${n}%`;
  if (fmt === 'int') return n.toLocaleString('en-IN');
  return String(n);
}

/**
 * The gap between the two, and whether it favours the merchant's own couriers.
 *
 * Returns null where no direction wins (volume rows), so the page renders a dash rather than
 * colouring a difference that means nothing.
 */
export function gapOf(metric) {
  if (!metric.better) return null;
  const diff = round(metric.own - metric.proship, 2);
  const ownWins = metric.better === 'high' ? diff > 0 : diff < 0;
  return { diff, ownWins, isFlat: Math.abs(diff) < 0.05 };
}
